import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from '../lib/supabase.js'

// useAiReceipts — lifecycle hook for the "AI Receipts — this week" card
// (Ship 9). House useState/useEffect pattern (NO React Query), mirroring
// useRollupBrief / useGroupReorderBrief.
//
// Scope picks the endpoint:
//   { restaurantId } → get-ai-receipts        (single-unit, body { restaurant_id })
//   { groupId }      → get-group-ai-receipts   (group,       body { group_id })
//
// These endpoints are DETERMINISTIC (no model call, no cost), so unlike the
// brief hooks there is no DB cache row — we just call the fn and keep a
// 5-MINUTE client freshness window (a ref timestamp) so re-mounts / tab
// switches don't re-fetch needlessly. An inFlightRef guards against double
// fires within a render cycle.
//
// Returns { phase, entries, generatedAt, totals, refresh }.
//   phase: 'idle' | 'loading' | 'ready' | 'empty' | 'error'

const FRESH_WINDOW_MS = 5 * 60 * 1000

export function useAiReceipts(scope) {
  const restaurantId = scope?.restaurantId || null
  const groupId = scope?.groupId || null

  // --- All hooks BEFORE any conditional return (React #310). ---
  const [phase, setPhase]       = useState('idle') // idle|loading|ready|empty|error
  const [entries, setEntries]   = useState([])
  const [totals, setTotals]     = useState({ total_actions_count: 0, total_dollars_impact: 0 })
  const [generatedAt, setGenAt] = useState(null)

  const inFlightRef = useRef(false)
  const lastFetchedRef = useRef(0)
  // Key the freshness window to the active scope so switching units/groups
  // forces a fresh fetch rather than reusing a stale window.
  const scopeKeyRef = useRef(null)

  const load = useCallback(async ({ force = false } = {}) => {
    if (!restaurantId && !groupId) return
    if (inFlightRef.current) return

    const scopeKey = restaurantId ? `r:${restaurantId}` : `g:${groupId}`
    const scopeChanged = scopeKeyRef.current !== scopeKey
    const fresh = !scopeChanged && Date.now() - lastFetchedRef.current < FRESH_WINDOW_MS
    if (fresh && !force) return

    inFlightRef.current = true
    setPhase('loading')
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const anonKey     = import.meta.env.VITE_SUPABASE_ANON_KEY
      const { data: sess } = await supabase.auth.getSession()
      const token = sess?.session?.access_token || anonKey

      const fnName = restaurantId ? 'get-ai-receipts' : 'get-group-ai-receipts'
      const payload = restaurantId ? { restaurant_id: restaurantId } : { group_id: groupId }

      const resp = await fetch(`${supabaseUrl}/functions/v1/${fnName}`, {
        method: 'POST',
        headers: {
          'content-type':  'application/json',
          'authorization': `Bearer ${token}`,
          'apikey':        anonKey,
        },
        body: JSON.stringify(payload),
      })
      if (!resp.ok) {
        const errText = await resp.text()
        throw new Error(`HTTP ${resp.status}: ${errText.slice(0, 200) || resp.statusText}`)
      }
      const data = await resp.json()
      if (data?.error) {
        setPhase('error')
        return
      }

      scopeKeyRef.current = scopeKey
      lastFetchedRef.current = Date.now()
      setGenAt(new Date().toISOString())

      if (data?._empty || !Array.isArray(data?.entries) || data.entries.length === 0) {
        setEntries([])
        setTotals({ total_actions_count: 0, total_dollars_impact: 0 })
        setPhase('empty')
        return
      }

      setEntries(data.entries)
      setTotals({
        total_actions_count: data.total_actions_count || data.entries.length,
        total_dollars_impact: data.total_dollars_impact || 0,
      })
      setPhase('ready')
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[useAiReceipts] fetch failed', e)
      // Deterministic calm fallback — never the raw error.
      setPhase('error')
    } finally {
      inFlightRef.current = false
    }
  }, [restaurantId, groupId])

  useEffect(() => {
    if (!restaurantId && !groupId) return
    load({ force: false })
  }, [restaurantId, groupId, load])

  const refresh = useCallback(() => load({ force: true }), [load])

  return useMemo(
    () => ({ phase, entries, generatedAt, totals, refresh }),
    [phase, entries, generatedAt, totals, refresh],
  )
}
